package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.OverviewResponse
import dev.lingvo.shared.Role
import dev.lingvo.shared.atLeast
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LLanguagesIcon
import dev.lingvo.site.i18n.t
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceSelection

/**
 * Whether the shell says "no translation provider is connected", from the dashboard's own answer.
 *
 * Stated as a function because it is a *rule*, and the rule is the thing that was wrong: the shell
 * and the project's provider cards were reading two different predicates, so a workspace could be
 * told machine translation was unavailable while two cards on its settings page said Ready and a
 * pre-translate against either would have run. `OverviewResponse.providerReady` is now the same
 * predicate the cards use (see `OverviewRoutes.buildOverview`), and this is the only term the
 * banner is allowed to have an opinion about.
 *
 * Null — the overview has not answered yet — draws nothing. So does "providers" among the
 * response's failed blocks, which
 * means the provider list did not answer at all: not knowing is not the same as not having one, and
 * a bar across every page is too loud for a reading that might simply be a dropped request.
 */
internal fun warnsNoProvider(overview: OverviewResponse?): Boolean {
    val answered = overview ?: return false
    return !answered.providerReady && "providers" !in answered.failed
}

/**
 * Says that this workspace has languages to fill and nothing to fill them with.
 *
 * **The one setup step the product cannot do for you, and the shell never mentioned it.** A
 * workspace can have projects, target languages, keys and an import pipeline and still have no
 * translation provider connected — in which case every automation in the app refuses, one screen at
 * a time, each with its own local explanation. `providerReady` is the workspace-level answer the
 * dashboard already computes (`OverviewResponse.providerReady`: any provider a project would
 * resolve to `READY`, which is the predicate the provider cards draw from), so the shell states it
 * once instead.
 *
 * **Only where the answer means something.** A workspace with no project, or with projects that
 * have no target language, has nothing to translate yet — telling those readers to connect a
 * provider is an instruction with no purpose behind it, and it is the state every brand-new
 * workspace starts in. The directory the trail already loads answers that without a request.
 *
 * **Refuses honestly.** An admin or owner gets the action; everyone else gets the fact and the name
 * of who holds the privilege, because `PUT /orgs/{slug}/providers/{id}` is admin-only and sending a
 * translator to a page that refuses them is worse than saying so.
 *
 * The overview request is the dashboard's own endpoint, and it is skipped entirely until the
 * directory says there is a target language somewhere in the workspace. A failure draws nothing:
 * "we could not tell whether you have a provider" is not a notice worth a bar across every page.
 */
@Composable
internal fun noProviderNotice(): ShellNotice? {
    val workspace = WorkspaceSelection.current() ?: return null
    val hasTargetLocale = ProjectDirectory.projects.any { project ->
        project.locales.any { it != project.sourceLocale }
    }
    if (!hasTargetLocale) return null

    val ctx = rememberPageContext()
    val overview = loadAsync(workspace.slug, hasTargetLocale) { ApiClient.overview(workspace.slug) }
    if (!warnsNoProvider((overview as? Async.Success)?.value)) return null

    val canConnect = workspace.role.atLeast(Role.ADMIN)
    return ShellNotice(
        id = "no-provider",
        title = t(
            "no_provider_banner.no_translation_provider_is_connected",
            "No translation provider is connected.",
        ),
        description = if (canConnect) {
            t(
                "no_provider_banner.machine_translation_stays_off_until_one",
                "Machine translation stays off until one is.",
            )
        } else {
            t(
                "no_provider_banner.an_owner_or_admin_connects_one",
                "An owner or admin connects one. Typing and review work either way.",
            )
        },
        variant = LAlertVariant.WARNING,
        action = if (canConnect) {
            {
                LButton(
                    label = t("no_provider_banner.connect_one", "Connect one"),
                    onClick = { ctx.router.tryRoutingTo("/app/settings/providers") },
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LLanguagesIcon() },
                )
            }
        } else {
            null
        },
    )
}
